import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const MAX_MB = 8;

/**
 * Drag-and-drop image uploader. Stores the file via POST /api/admin/uploads/image
 * and returns the resulting URL via onChange. Falls back to a plain text input
 * so editors can still paste an external URL.
 *
 *   <ImageUploader value={draft.cover_image} onChange={(url) => setDraft(...)} testId="cover" />
 */
export default function ImageUploader({
  value,
  onChange,
  testId = "image",
  label,
  hint,
  aspect = "aspect-[16/9]",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (jpg, png, webp, gif, svg).");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (f) upload(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) upload(f);
  };

  const clear = () => onChange("");

  return (
    <div className="space-y-2" data-testid={`image-uploader-${testId}`}>
      {label && (
        <label className="font-mono uppercase text-[11px] tracking-widest block">
          {label}
        </label>
      )}

      {value ? (
        <div className="brut bg-white relative group">
          <div className={`w-full ${aspect} overflow-hidden`}>
            <img
              src={value}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              data-testid={`image-uploader-${testId}-preview`}
            />
          </div>
          <button
            type="button"
            onClick={clear}
            data-testid={`image-uploader-${testId}-clear`}
            className="brut bg-white absolute top-2 right-2 px-2 py-1 font-mono uppercase text-[10px] tracking-widest brut-press inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition"
            title="Remove image"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid={`image-uploader-${testId}-dropzone`}
          disabled={busy}
          className={`brut w-full ${aspect} flex flex-col items-center justify-center gap-2 font-mono uppercase text-[11px] tracking-widest transition ${
            dragOver ? "pcs-bg-blue text-white" : "bg-white hover:pcs-bg-cream"
          } ${busy ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
        >
          {busy ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" strokeWidth={2} />
              <span>Uploading…</span>
            </>
          ) : (
            <>
              <Upload className="w-6 h-6" strokeWidth={2} />
              <span>Drag image or click to upload</span>
              <span className="opacity-60 normal-case tracking-normal text-[10px]">
                jpg · png · webp · gif · svg · max {MAX_MB} MB
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onPick}
        className="hidden"
        data-testid={`image-uploader-${testId}-input`}
      />

      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="…or paste an image URL"
          data-testid={`image-uploader-${testId}-url`}
          className="flex-1 brut bg-white px-3 py-2 focus:outline-none text-sm font-mono"
        />
      </div>

      {hint && (
        <div className="font-mono text-[10px] opacity-60">{hint}</div>
      )}
    </div>
  );
}
